/**
 * Voice command HTTP client (V1-CLIENT-SAFETY).
 *
 * Generates durable command identities for the canonical `/voice/command` flow.
 *
 * Legacy server compatibility (do not guess silently):
 * - New clients always send `command_id`, `ui_session_id`, and `source`.
 * - During the Wave 0 migration window (`VOICE_LEGACY_COMMAND_ID.MIGRATION_WINDOW_DAYS`),
 *   servers that still accept requests without `command_id` may mint one and warn with
 *   `VOICE_LEGACY_COMMAND_ID_ASSIGNED`. See `client/src/voice/constants.ts` and
 *   `docs/03-design/lld-voice-system.md` § Legacy command_id Compatibility.
 * - Confirmation must echo the original mutation via `pending_command_id` — never mint a
 *   new mutation identity for confirm/deny.
 */

import { API_BASE_URL } from '../config/api';
import type { ConfirmCardData } from '../components/VoiceConfirmCard';
import type { VoiceCommandSource, VoiceCommandRequest } from '../voice/types';
import { VOICE_LEGACY_COMMAND_ID, VOICE_PENDING_TTL, VOICE_STORAGE_SUFFIXES } from '../voice/constants';
import type { VoiceMessage } from './voiceSessionStorage';
import { getPendingContactConfirmation, getPendingContactDisambiguation } from './voicePendingConfirmation';
import { getPendingDocumentFlow } from './voicePendingDocumentFlow';
import {
  migrateLegacyVoiceStorage,
  readVoiceKey,
  removeVoiceKey,
  resolveVoiceStorageScope,
  writeVoiceKey,
} from '../voice/voiceStorage';

export interface VoiceCommandClientState {
  isOpen: boolean;
  isMinimized: boolean;
  isRecording: boolean;
  messages: VoiceMessage[];
  assistantContext?: Record<string, unknown>;
}

export interface VoiceCommandResult {
  success?: boolean;
  message?: string;
  action?: string;
  phase?: string;
  command_id?: string;
  session_id?: string;
  nextQuestion?: string;
  missingFields?: string[];
  needsConfirmation?: boolean;
  pendingAction?: { data?: Record<string, unknown> };
  pending_action?: { command_id?: string; data?: Record<string, unknown> };
  data?: any;
  shared_mind?: any;
  suggestions?: unknown[];
  next_steps?: unknown[];
  conversation_state?: {
    original_intent?: string;
    current_intent?: string;
    status?: string;
  };
  error_code?: string;
}

export interface VoiceCommandSendOptions {
  /** Optional capture identity from the microphone capture engine. */
  capture_id?: string;
  /** Defaults to microphone/text inference; pass explicitly when known. */
  source?: VoiceCommandSource;
  /** Echo of the original mutation command when confirming/denying. */
  pending_command_id?: string;
  /** True when the user explicitly confirms a pending mutation. */
  confirmed?: boolean;
  /** Override generated command_id (tests / rare replay). */
  command_id?: string;
  /** When true, treat this request as confirmation even if text is free-form. */
  isConfirmation?: boolean;
}

const UI_SESSION_STORAGE_KEY = 'voiceUiSessionId';
const PENDING_COMMAND_STORAGE_KEY = 'voicePendingCommandId';
const BACKEND_SESSION_LEGACY_KEY = 'voiceSessionId';

// Keep aligned with server conversationSignals: cancel/deny/affirm phrases must
 // attach pending_command_id. Bare "cancel" alone is not enough — users say
 // "cancel all this", "never mind", "forget it", etc.
const CONFIRMATION_TEXT_PATTERN =
  /^(yes(\s+confirm)?|confirm(ed)?|sure|ok(ay)?|proceed|go\s+ahead)\b/i;

function withScope() {
  const scope = resolveVoiceStorageScope();
  if (scope) migrateLegacyVoiceStorage(scope);
  return scope;
}

export const createVoiceCommandId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cmd_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

export const ensureVoiceSessionId = (): string => {
  const scope = withScope();
  if (scope) {
    const existing = readVoiceKey<string>(scope, VOICE_STORAGE_SUFFIXES.backendSession);
    if (existing) return existing;
    const sessionId = `voice_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    writeVoiceKey(
      scope,
      VOICE_STORAGE_SUFFIXES.backendSession,
      sessionId,
      VOICE_PENDING_TTL.MAX_MS
    );
    return sessionId;
  }
  // Pre-auth fallback (should be rare)
  let sessionId = localStorage.getItem(BACKEND_SESSION_LEGACY_KEY);
  if (!sessionId) {
    sessionId = `voice_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(BACKEND_SESSION_LEGACY_KEY, sessionId);
  }
  return sessionId;
};

export const clearVoiceBackendSessionId = (): void => {
  const scope = withScope();
  if (scope) removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.backendSession);
  localStorage.removeItem(BACKEND_SESSION_LEGACY_KEY);
};

export const ensureVoiceUiSessionId = (): string => {
  let uiSessionId = localStorage.getItem(UI_SESSION_STORAGE_KEY);
  if (!uiSessionId) {
    uiSessionId = createVoiceCommandId();
    localStorage.setItem(UI_SESSION_STORAGE_KEY, uiSessionId);
  }
  return uiSessionId;
};

/** Rotate on new chat, business switch, logout, or conversation replace. */
export const rotateVoiceUiSessionId = (): string => {
  const uiSessionId = createVoiceCommandId();
  localStorage.setItem(UI_SESSION_STORAGE_KEY, uiSessionId);
  return uiSessionId;
};

export const storePendingCommandId = (commandId: string): void => {
  if (!commandId) return;
  const scope = withScope();
  if (scope) {
    const existing =
      readVoiceKey<{ confirmation?: unknown; disambiguation?: unknown; pendingCommandId?: string }>(
        scope,
        VOICE_STORAGE_SUFFIXES.pendingAction
      ) || {};
    writeVoiceKey(
      scope,
      VOICE_STORAGE_SUFFIXES.pendingAction,
      { ...existing, pendingCommandId: commandId },
      VOICE_PENDING_TTL.DEFAULT_MS
    );
  }
  // Mirror to legacy key for cross-tab readers that have not migrated yet —
  // teardown discards this and it is never promoted from legacy migration.
  localStorage.setItem(PENDING_COMMAND_STORAGE_KEY, commandId);
};

export const getPendingCommandId = (): string | null => {
  const scope = withScope();
  if (scope) {
    const bundle = readVoiceKey<{ pendingCommandId?: string; expiresAt?: number }>(
      scope,
      VOICE_STORAGE_SUFFIXES.pendingAction
    );
    if (bundle?.pendingCommandId) return bundle.pendingCommandId;
  }
  return localStorage.getItem(PENDING_COMMAND_STORAGE_KEY);
};

export const clearPendingCommandId = (): void => {
  const scope = withScope();
  if (scope) {
    const existing = readVoiceKey<Record<string, unknown>>(
      scope,
      VOICE_STORAGE_SUFFIXES.pendingAction
    );
    if (existing && 'pendingCommandId' in existing) {
      const { pendingCommandId: _removed, ...rest } = existing as {
        pendingCommandId?: string;
      } & Record<string, unknown>;
      if (Object.keys(rest).length === 0) {
        removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingAction);
      } else {
        writeVoiceKey(
          scope,
          VOICE_STORAGE_SUFFIXES.pendingAction,
          rest,
          VOICE_PENDING_TTL.DEFAULT_MS
        );
      }
    }
  }
  localStorage.removeItem(PENDING_COMMAND_STORAGE_KEY);
};

const looksLikeConfirmationText = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (CONFIRMATION_TEXT_PATTERN.test(trimmed)) return true;
  // Cancel / abort (including "cancel all this")
  if (/^(cancel|stop|abort|quit|exit)\b/i.test(trimmed)) return true;
  if (/\bnever\s*mind\b/i.test(trimmed)) return true;
  if (/\bforget (it|about it|that)\b/i.test(trimmed)) return true;
  if (/\b(scrap|drop|leave) (it|that)\b/i.test(trimmed)) return true;
  if (/\bstart over\b/i.test(trimmed)) return true;
  // Deny
  if (/^(no|nope|nah|naw|deny|rejected?)(\b|$)/i.test(trimmed)) return true;
  if (/\bno\s+thanks?\b/i.test(trimmed)) return true;
  if (/^(that'?s )?(wrong|incorrect|not right|not correct)\b/i.test(trimmed)) return true;
  return false;
};

const resolveSource = (
  text: string,
  options?: VoiceCommandSendOptions
): VoiceCommandSource => {
  if (options?.source) return options.source;
  if (options?.isConfirmation || options?.confirmed || options?.pending_command_id) {
    return 'confirmation';
  }
  if (getPendingCommandId() && looksLikeConfirmationText(text)) {
    return 'confirmation';
  }
  return 'text';
};

const resolvePendingCommandId = (
  text: string,
  options?: VoiceCommandSendOptions
): string | undefined => {
  if (options?.pending_command_id) return options.pending_command_id;
  const source = resolveSource(text, options);
  if (source === 'confirmation' || options?.confirmed || options?.isConfirmation) {
    return getPendingCommandId() || undefined;
  }
  return undefined;
};

export const buildVoiceCommandRequest = (
  text: string,
  sessionId: string,
  state: VoiceCommandClientState,
  options?: VoiceCommandSendOptions
): VoiceCommandRequest => {
  const currentPage = window.location.pathname || '/dashboard';
  const currentSearch = window.location.search || '';
  const gvaState: any = (window as any).__GVA_STATE__ || {};
  const pendingContactConfirmation = getPendingContactConfirmation();
  const pendingContactDisambiguation = getPendingContactDisambiguation();
  const pendingDocumentFlow = getPendingDocumentFlow();
  const assistantContext = state.assistantContext ?? gvaState.assistantContext;
  const initiatedFrom = assistantContext?.initiatedFrom;
  const source = resolveSource(text, options);
  const pendingCommandId = resolvePendingCommandId(text, options);
  const confirmed =
    options?.confirmed === true
    || (source === 'confirmation' && /^(yes(\s+confirm)?|confirm(ed)?)$/i.test(text.trim()));

  return {
    command_id: options?.command_id || createVoiceCommandId(),
    ...(options?.capture_id ? { capture_id: options.capture_id } : {}),
    ui_session_id: ensureVoiceUiSessionId(),
    session_id: sessionId,
    source,
    voice_text: text,
    current_page: `${currentPage}${currentSearch}`,
    page_context: {
      url: `${currentPage}${currentSearch}`,
      pathname: currentPage,
      search: currentSearch,
      title: document.title,
      timestamp: new Date().toISOString(),
      ...(assistantContext ? { assistant_context: assistantContext } : {}),
      ...(initiatedFrom ? { initiated_from: initiatedFrom } : {}),
    },
    client_state: {
      current_page: `${currentPage}${currentSearch}`,
      initiated_from: initiatedFrom || null,
      pending_contact_confirmation: pendingContactConfirmation,
      pending_contact_disambiguation: pendingContactDisambiguation,
      pending_document_flow: pendingDocumentFlow,
      ui_state: {
        is_open: state.isOpen,
        is_minimized: state.isMinimized,
        is_recording: state.isRecording,
        message_count: state.messages.length,
      },
      selected_items: gvaState.selectedItems || [],
      filters: gvaState.filters || {},
      recent_interactions: state.messages.slice(-5).map((message) => ({
        role: message.role,
        content: message.content.substring(0, 100),
        timestamp: message.timestamp,
      })),
    },
    ...(pendingCommandId ? { pending_command_id: pendingCommandId } : {}),
    ...(confirmed ? { confirmed: true } : {}),
    created_at: Date.now(),
  };
};

export const requestServerFastNavigation = async (text: string) => {
  const response = await fetch(`${API_BASE_URL}/voice/navigate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify({ voice_text: text }),
  });
  if (!response.ok) return null;
  const result = await response.json();
  return result.success && result.fast_track ? result : null;
};

const shouldStorePendingCommand = (result: VoiceCommandResult): boolean =>
  result.needsConfirmation === true
  || result.phase === 'confirmation'
  || result.phase === 'contact_confirmation'
  || !!result.pendingAction?.data
  || !!result.pending_action;

export const sendVoiceCommand = async (
  text: string,
  sessionId: string,
  state: VoiceCommandClientState,
  options?: VoiceCommandSendOptions
): Promise<VoiceCommandResult> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Please log in to use voice commands.');

  const request = buildVoiceCommandRequest(text, sessionId, state, options);

  const response = await fetch(`${API_BASE_URL}/voice/command`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    const serverMessage =
      typeof data.error === 'string'
        ? data.error
        : typeof data.message === 'string'
          ? data.message
          : '';
    if (serverMessage) throw new Error(serverMessage);
    if (response.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }
    throw new Error(`Request failed (${response.status})`);
  }

  const result: VoiceCommandResult = await response.json();

  if (shouldStorePendingCommand(result)) {
    storePendingCommandId(result.command_id || request.command_id);
  } else if (
    request.source === 'confirmation'
    || request.confirmed
    || result.phase === 'cancelled'
    || result.action === 'cancelled'
  ) {
    clearPendingCommandId();
  }

  return result;
};

export const storeSharedMind = (result: VoiceCommandResult): void => {
  if (!result.shared_mind) return;
  const { unified_context, predicted_action, proactive_suggestions } = result.shared_mind;
  const gvaState: any = (window as any).__GVA_STATE__ || ((window as any).__GVA_STATE__ = {});
  gvaState.sharedMind = {
    unified_context,
    predicted_action,
    proactive_suggestions,
    session_id: result.session_id,
  };
};

export const getProactiveMessages = (result: VoiceCommandResult): Array<{ content: string; delay: number }> => {
  const messages: Array<{ content: string; delay: number }> = [];
  // Only surface contextual follow-up questions — never inject page tips like
  // "Create a sales invoice" into the chat (those belong in suggested-query chips).
  const { predicted_action } = result.shared_mind || {};
  if (
    result.success
    && predicted_action?.confidence >= 0.7
    && predicted_action.suggestion
  ) {
    messages.push({ content: predicted_action.suggestion, delay: 2000 });
  }
  return messages;
};

export const createVoiceConfirmCard = (
  result: VoiceCommandResult,
  onConfirm: () => void,
  onCancel: () => void
): ConfirmCardData | null => {
  if (!result.needsConfirmation || !result.pendingAction?.data) return null;

  const pendingId =
    result.command_id
    || result.pending_action?.command_id
    || getPendingCommandId();
  if (pendingId) storePendingCommandId(pendingId);

  const data = result.pendingAction.data;
  return {
    title: result.message?.includes('invoice') ? 'Confirm Invoice' : 'Confirm Action',
    fields: Object.entries(data)
      .filter(([key]) => !['business_id', 'is_active'].includes(key))
      .slice(0, 6)
      .map(([key, value]) => ({
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
        value: String(value ?? ''),
      })),
    onConfirm,
    onCancel,
  };
};

export const getVoiceMessageExtras = (result: VoiceCommandResult) => ({
  category:
    result.action === 'navigate'
      ? undefined
      : result.data?.invoice
        ? 'Financial'
        : result.data?.report
          ? 'Reports'
          : undefined,
  card: result.data?.invoice
    ? {
        type: 'invoice' as const,
        title: result.data.invoice.number || 'Invoice Draft',
        subtitle: result.data.invoice.customer,
        amount: result.data.invoice.amount,
        status: 'DRAFT',
      }
    : undefined,
});

export const isFeedbackBlocked = (result: VoiceCommandResult): boolean =>
  result.phase === 'feedback_blocked' || result.message === '';

export const isIntentFullyExecuted = (result: VoiceCommandResult): boolean => {
  const message = (result.message || '').trim();
  if (
    message === 'Transaction processed. Please review.'
    || message === 'Done.'
    || result.action === 'clarify'
    || result.phase === 'gathering_quote'
    || result.data?.phase === 'gathering_quote'
  ) {
    return false;
  }

  return (
    result.success === true
    && !result.phase
    && !result.needsConfirmation
    && !!message
    && ['successfully', 'completed', 'created', 'recorded', 'converted', 'has been'].some((word) =>
      message.toLowerCase().includes(word)
    )
  );
};

export interface VoiceQuickPickOption {
  id: number;
  name: string;
  index: number;
  quote_number?: string;
}

export const extractVoiceQuickPicks = (result: VoiceCommandResult): VoiceQuickPickOption[] => {
  const rawCustomers = result.data?.suggested_customers;
  if (Array.isArray(rawCustomers)) {
    const customers = rawCustomers.filter(
      (option): option is VoiceQuickPickOption =>
        !!option
        && typeof option.id === 'number'
        && typeof option.name === 'string'
        && typeof option.index === 'number'
    );
    if (customers.length > 0) return customers;
  }

  const rawSuppliers = result.data?.suggested_suppliers;
  if (Array.isArray(rawSuppliers)) {
    const suppliers = rawSuppliers.filter(
      (option): option is VoiceQuickPickOption =>
        !!option
        && typeof option.id === 'number'
        && typeof option.name === 'string'
        && typeof option.index === 'number'
    );
    if (suppliers.length > 0) return suppliers;
  }

  const rawQuotes = result.data?.suggested_quotes;
  if (!Array.isArray(rawQuotes)) return [];
  return rawQuotes
    .filter(
      (option): option is VoiceQuickPickOption =>
        !!option
        && typeof option.id === 'number'
        && typeof option.name === 'string'
        && typeof option.index === 'number'
    )
    .map((option) => ({
      ...option,
      quote_number: typeof option.quote_number === 'string' ? option.quote_number : undefined,
    }));
};

export const shouldCloseVoiceAssistant = (result: VoiceCommandResult): boolean =>
  !!result.data?.quote_id
  || !!result.data?.invoice_id
  || (!!result.data?.quote_number
    && (result.message?.toLowerCase().includes('has been created successfully') ?? false))
  || (!!result.data?.invoice_number
    && (result.message?.toLowerCase().includes('has been created') ?? false));

export const needsContinuation = (result: VoiceCommandResult): boolean =>
  result.phase === 'gathering_data'
  || result.phase === 'gathering_customer'
  || result.phase === 'gathering_quote'
  || result.phase === 'gathering_items'
  || result.phase === 'confirmation'
  || result.phase === 'contact_confirmation'
  || result.phase === 'disambiguation'
  || result.data?.phase === 'gathering_quote'
  || result.needsConfirmation === true
  || !!(
    result.message
    && (result.message.includes('?')
      || result.message.toLowerCase().includes('what')
      || result.message.toLowerCase().includes('please')
      || result.message.toLowerCase().includes('need')
      || result.message.toLowerCase().includes('required'))
  );

/** Re-export for callers documenting legacy window behavior. */
export { VOICE_LEGACY_COMMAND_ID };
